import { useCallback, useRef } from 'react';

interface UseLongPressOptions {
  onLongPress: () => void;
  onClick?: () => void;
  duration?: number;
}

export function useLongPress({ onLongPress, onClick, duration = 500 }: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTriggered = useRef(false);
  const movedBeyondThreshold = useRef(false);
  const startPoint = useRef<{ x: number; y: number } | null>(null);

  const MOVEMENT_THRESHOLD_PX = 10;

  const readPoint = (event: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in event) {
      const touch = event.touches[0] || event.changedTouches[0];
      return touch ? { x: touch.clientX, y: touch.clientY } : null;
    }

    return { x: event.clientX, y: event.clientY };
  };

  const start = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    isTriggered.current = false;
    movedBeyondThreshold.current = false;
    startPoint.current = readPoint(event);
    timerRef.current = setTimeout(() => {
      isTriggered.current = true;
      onLongPress();
    }, duration);
  }, [onLongPress, duration]);

  const end = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!isTriggered.current && !movedBeyondThreshold.current && onClick) {
      onClick();
    }
    isTriggered.current = false;
    movedBeyondThreshold.current = false;
    startPoint.current = null;
  }, [onClick]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isTriggered.current = false;
    startPoint.current = null;
  }, []);

  const handleMove = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    const origin = startPoint.current;
    const current = readPoint(event);

    if (!origin || !current) {
      return;
    }

    const deltaX = current.x - origin.x;
    const deltaY = current.y - origin.y;
    const distance = Math.hypot(deltaX, deltaY);

    if (distance > MOVEMENT_THRESHOLD_PX) {
      movedBeyondThreshold.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: end,
    onTouchMove: handleMove,
    onTouchCancel: cancel,
    onMouseDown: start,
    onMouseMove: handleMove,
    onMouseUp: end,
    onMouseLeave: cancel,
  };
}
